import { get } from 'lodash';

/**
 * The definitive order of fashion sizes. The index of each size
 * in this array determines its sorting rank.
 */
export const ORDERED_SIZES: readonly string[] = [
  'xxxxxxxs',
  '7xs',
  'xxxxxxs',
  '6xs',
  'xxxxxs',
  '5xs',
  'xxxxs',
  '4xs',
  'xxxs',
  '3xs',
  'xxs',
  '2xs',
  'xs',
  'xs/s',
  's',
  's/m',
  'm',
  'm/l',
  'l',
  'l/xl',
  'xl',
  'xxl',
  '2xl',
  'xxxl',
  '3xl',
  'xxxxl',
  '4xl',
  'xxxxxl',
  '5xl',
  'xxxxxxl',
  '6xl',
  'xxxxxxxl',
  '7xl',
];

/**
 * A Map for O(1) lookup of a size's sorting rank. Case-insensitive.
 */
const SIZE_RANK_MAP = new Map<string, number>();
ORDERED_SIZES.forEach((size, index) => {
  SIZE_RANK_MAP.set(size.toLowerCase(), index);
});

export const isAbbreviatedSize = (title: string | null | undefined) =>
  title && SIZE_RANK_MAP.has(title?.toLowerCase());

/**
 * A list of sizes sorted by length descending. This is crucial for matching,
 * ensuring that 'l/xl' is found before 'l' or 'xl' in partial matches.
 */
const MATCHING_PRIORITY_LIST = [...ORDERED_SIZES].sort(
  (a, b) => b.length - a.length,
);

/**
 * Sorting tiers. Lower numbers have higher priority.
 */
const enum TIER {
  WEIGHTED = 0,
  NUMERIC = 1,
  PARTIAL_WEIGHTED_MATCH = 2,
  ALPHANUMERIC = 3,
}

// Regex to identify strings that consist only of digits.
const NUMERIC_REGEX = /^\d*\.?\d*$/;

type Categorized =
  | {
      tier: TIER.WEIGHTED | TIER.NUMERIC | TIER.PARTIAL_WEIGHTED_MATCH;
      value: number;
      groupKey: string;
    }
  | {
      tier: TIER.ALPHANUMERIC;
      value: string;
      groupKey: string;
    };

/**
 * Categorizes a string value into a tier and parses it for sorting.
 * @param value The string to categorize.
 * @returns An object with the tier, a parsed value for comparison, and a groupKey.
 */
function categorize(value: string): Categorized {
  const lowerValue = value.toLowerCase();

  // Tier 0: Check for an exact, weighted size match.
  if (SIZE_RANK_MAP.has(lowerValue)) {
    return {
      tier: TIER.WEIGHTED,
      value: SIZE_RANK_MAP.get(lowerValue)!,
      groupKey: '',
    };
  }

  // Tier 1: Check if it's a purely numeric string.
  if (NUMERIC_REGEX.test(value)) {
    return {
      tier: TIER.NUMERIC,
      value: parseInt(value, 10),
      groupKey: value,
    };
  }

  // Tier 2: Check for partial matches (starts/ends with a known size).
  for (const size of MATCHING_PRIORITY_LIST) {
    const sizeLower = size.toLowerCase();
    const matchesPrefix = lowerValue.startsWith(sizeLower);
    const matchesSuffix = lowerValue.endsWith(sizeLower);
    if (matchesPrefix || matchesSuffix) {
      // Determine the non-size segment (group key) to group partial matches together
      const groupKey = matchesPrefix
        ? lowerValue.slice(sizeLower.length)
        : lowerValue.slice(0, lowerValue.length - sizeLower.length);

      return {
        tier: TIER.PARTIAL_WEIGHTED_MATCH,
        value: SIZE_RANK_MAP.get(sizeLower)!, // rank of the matched size
        groupKey,
      } as const;
    }
  }

  // Tier 3: Default to standard alphanumeric.
  return {
    tier: TIER.ALPHANUMERIC,
    value: value,
    groupKey: value,
  };
}

type SortableItem = string | object;
type SortDirection = 'asc' | 'desc';

const getSizeFromKey = <T extends object>(item: T, key: string) => {
  const value = get(item, key);
  return typeof value === 'string' ? value.toLowerCase() : undefined;
};

// --- The Main Exported Function ---
/**
 * Sorts an array based on a multi-tiered logic for fashion sizes.
 *
 * @param array The array to sort. Can be string[] or object[].
 * @param key (Optional) The property key to use if the array contains objects.
 * @param direction (Optional) The sort direction, 'asc' (default) or 'desc'.
 * @returns A new array with the items sorted.
 */
export function sizeSorter<T extends SortableItem>(
  array: T[],
  key?: string,
  direction: SortDirection = 'asc',
): T[] {
  const directionMultiplier = direction === 'asc' ? 1 : -1;

  return [...array].sort((a, b) => {
    const valueA =
      key && typeof a === 'object' ? getSizeFromKey(a, key) : (a as string);
    const valueB =
      key && typeof b === 'object' ? getSizeFromKey(b, key) : (b as string);
    const stringA = valueA ? valueA.trim() : '';
    const stringB = valueB ? valueB.trim() : '';

    const catA = categorize(stringA);
    const catB = categorize(stringB);

    // 1. Primary sort: by tier
    const tierDiff = catA.tier - catB.tier;
    if (tierDiff !== 0) {
      return tierDiff * directionMultiplier;
    }

    // 2. Secondary sort: handle special logic per tier
    if (
      catA.tier === TIER.PARTIAL_WEIGHTED_MATCH &&
      catB.tier === TIER.PARTIAL_WEIGHTED_MATCH
    ) {
      // Group by the non-size segment (prefix and postfix to the weighted sizes)
      const groupKeyDiff = catA.groupKey.localeCompare(catB.groupKey);
      if (groupKeyDiff !== 0) return groupKeyDiff * directionMultiplier;

      // Within the same group, sort by the matched size rank
      const matchedRankDiff = catA.value - catB.value;
      if (matchedRankDiff !== 0) return matchedRankDiff * directionMultiplier;
    } else if (
      catA.tier === TIER.ALPHANUMERIC &&
      catB.tier === TIER.ALPHANUMERIC
    ) {
      // Alpha tier: localeCompare with numeric option
      const alphaDiff = catA.value.localeCompare(catB.value);
      if (alphaDiff !== 0) return alphaDiff * directionMultiplier;
    } else {
      // WEIGHTED and NUMERIC tiers: compare numeric values
      const valueDiff = (catA.value as number) - (catB.value as number);
      if (valueDiff !== 0) return valueDiff * directionMultiplier;
    }

    // 3. Tertiary sort (tie-breaker): If all above are identical, stabilize by original strings
    return stringA.localeCompare(stringB) * directionMultiplier;
  });
}
